// Invito ad attivare le notifiche push sul dispositivo (Angelo, 4/11/2026): una sola volta per browser, dentro la campanella, senza un altro modale all'accesso.
// «Visto» si ricorda in `$localStorage` (chiave con il suffisso del progetto): la push è per browser, quindi un browser nuovo ha il suo invito. Se lo storage non si può
// leggere o scrivere (navigazione privata, dati bloccati) l'invito non si mostra: senza memoria tornerebbe a ogni apertura.
import { useI18n } from "vue-i18n";

export const CHIAVE_INVITO_PUSH = "invitoPushVisto";

export function useInvitoPush() {
  const push = usePushStore();
  const { $localStorage } = useNuxtApp();
  const { toastSuccess, toastError } = useToastHelper();
  const { t } = useI18n();

  // Finché non si è letto il flag, niente invito (nessun lampo)
  const visto = ref(true);
  function leggi(): boolean {
    try {
      return $localStorage.getItem(CHIAVE_INVITO_PUSH) === "1";
    } catch {
      return true;
    }
  }
  function segnaVisto(): void {
    visto.value = true;
    try {
      $localStorage.setItem(CHIAVE_INVITO_PUSH, "1");
    } catch {
      // storage non disponibile: per questa sessione l'invito resta chiuso
    }
  }

  onMounted(async () => {
    await push.inizializza();
    visto.value = leggi();
  });

  /** Il browser può attivare le push e l'utente non è ancora iscritto. */
  const attivabile = computed(() => !visto.value && push.servePermesso && !push.iscritto);
  /** iOS fuori dalla PWA installata: non si può attivare, si spiega cosa fare. */
  const daInstallare = computed(() => !visto.value && push.serveInstallazionePwa);
  const visibile = computed(() => attivabile.value || daInstallare.value);

  /** IMPORTANTE (iOS): chiamarla direttamente dal clic, senza `await` prima di `richiediPermesso`. */
  async function attiva(): Promise<void> {
    const concesso = await push.richiediPermesso().catch(() => false);
    segnaVisto();
    if (!concesso) return;
    try {
      await push.iscrivi();
      toastSuccess("centro_notifiche.invito_push.attivato");
    } catch {
      toastError(t("centro_notifiche.invito_push.errore"));
    }
  }

  return { visibile, attivabile, daInstallare, inCorso: computed(() => push.inCorso), attiva, nonOra: segnaVisto };
}
